import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AbstractDialog, {AbstractDialogProperties, AbstractDialogState} from "./AbstractDialog";
import Translate from "../../translation/Translate";
import Icon from "../../components/Icon";
import FunctionHelper from "../../helper/FunctionHelper";
import {InputSwitch} from "primereact/inputswitch";
import StorageItem from "../../StorageItem";

export interface ConfirmDialogState extends AbstractDialogState {
	storeDecision: boolean;
}

export interface ConfirmDialogProperties extends AbstractDialogProperties {
	onConfirm?: () => Promise<void>;
	storeDecision?: StorageItem;
	message?: ReactNode;
}

export class ConfirmDialogDefinition extends AbstractDialog<ConfirmDialogProperties, ConfirmDialogState> {
	constructor(props: Readonly<ConfirmDialogProperties>) {
		super(props);

		this.setState({
			...this.state,
			storeDecision: false
		})
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["confirm-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">{this.props.message}</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(this.props.storeDecision)}>
							<div className="p-col-12 switch-container">
								<InputSwitch inputId="control-store-decision" checked={this.state.storeDecision}
											 onChange={(e) => this.setState({
												 storeDecision: FunctionHelper.applyValue(e.value, false)
											 })}/>
								<label className="switch-label" htmlFor="control-store-decision">
									<Translate {...this.props} data="confirm.store" namespace="dialog"/>
								</label>
							</div>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-help"}/>
					<Translate {...this.props} data="confirm.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div>
				<Button label={Translate.value("buttons.confirm")} disabled={!this.isValid()}
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
						className="primary-button" onClick={() => this.submit()}/>
				{this.state.submitted ?
					<Button label={Translate.value("buttons.close")} icon="icon icon-close"
							onClick={() => this.hide()} className="dialog-button"/> :
					<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
							onClick={() => this.hide()} className="dialog-button"/>
				}
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected async submit(): Promise<void> {
		super.submit();

		if (FunctionHelper.exists(this.props.storeDecision) && this.state.storeDecision) {
			sessionStorage.setItem(this.props.storeDecision!, "true");
		}

		if (FunctionHelper.exists(this.props.onConfirm)) {
			try {
				await this.props.onConfirm!();
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}
		}

		this.hide();
	}
}

const RefreshDialog = React.memo(ConfirmDialogDefinition,
	(prevProps: Readonly<ConfirmDialogProperties>, nextProps: Readonly<ConfirmDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RefreshDialog;